import React, { useCallback, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, Grid, MenuItem, TextField } from '@mui/material';

import withProps from '@dyl/utils/withProps';
import Conditional from '@dyl/components/Conditional';
import LoadingScreen from '@dyl/components/LoadingScreen';

import useLogic from './logic';
import useStyles from './styles';
import ComponentPicker from './ComponentPicker';
import OutputComponentList from './OutputComponentList';
import ScreenSaveCancelBlock from '@dyl/blocks/ScreenSaveCancelBlock';
import withLevel from "@dyl/utils/withLevel";
import { exportKeys } from "@dyl/utils/exportKeys";
import { getTestComponent } from "@dyl/apiClients/backendApi";
import ActivityLogs from '@dyl/components/ActivityLogs';
import SectionDividers from '@dyl/components/SectionDividers';


export default withLevel(TestEditor);

/**
 * @param {Object} props
 * @param {boolean} props.isNew
 * @param {string} props.testId
 * @param {string} props.level
 * @param {string} props.qpvSet
 */
function TestEditor(props) {
    const {
        isLoading,

        levels: rawLevels,
        level,
        onLevelChange,
        allowLevelChange,

        name,
        onNameChange,
        onNameBlur,
        onNamePaste,
        onNameInput,

        allowContentEditing,
        allowAddContent,
        componentOptions,

        selectedComponents,
        selectedComponentIds,
        setSelectedComponentIds,
        removeComponent,

        allowSaving,
        saveTest,
        isSaving,
        nameError,

        qpvSet,
        onQpvSetChange,

        status,

    } = useLogic(props);

    const { globalLevel, testId = undefined, isNew } = props;
    const classes = useStyles(props);

    const levels = useMemo(() => [
        { level_id: '', label: 'Chose a level' },
        ...rawLevels
    ], [rawLevels]);

    const Dropdown = useMemo(() => withProps(TextField, {
        // className: classes.textField,
        variant: 'filled',
        select: true,
        fullWidth: true
    }), []);

    const [isAddDialogOpen, setAddDialogOpen] = useState(false);
    const openAddDialog = useCallback(() => setAddDialogOpen(true), []);

    const onExportKeys = useCallback(async () => {
        let components = selectedComponents.filter(c => c.skill_id !== 'speaking' || c.level !== 'A1');
        await Promise.all(
            components.map(async (component) => {
                const tc = await getTestComponent(component.level_id, component.skill_id, component.test_id);
                console.log(tc);
                await exportKeys(globalLevel, component.test_name)(tc.tasks);
            }));
    }, [level, selectedComponents]);

    return (
        <Box className={classes.root}>
            {/* Dropdown row */}
            <Grid container spacing={3}>
                {/* Choose a level */}
                <Grid item xs={2}>
                    <Dropdown
                        id="test-level"
                        label="Level"
                        value={level}
                        onChange={onLevelChange}
                        helperText={'e.g. Pre-A1'}
                        disabled={!allowLevelChange}
                    >
                        {levels.map(({ level_id, label }) => (
                            <MenuItem key={level_id} value={level_id}>
                                {label}
                            </MenuItem>
                        ))}
                    </Dropdown>
                </Grid>
                <Grid item xs={4}>
                    <TextField
                        id="qpv-set"
                        label="QPV Set (SAP test reference)"
                        variant="filled"
                        fullWidth
                        value={qpvSet}
                        onChange={onQpvSetChange}
                        InputLabelProps={{
                            shrink: Boolean(qpvSet),
                            'data-shrink': Boolean(qpvSet),
                        }}
                        disabled={status === 'live'}
                    />
                </Grid>
            </Grid>

            {/* Name & add button */}
            {/* NB: Visually, name is called 'ID' */}
            <Grid container spacing={3}>
                {/* Name */}
                <Grid item xs={6}>
                    <TextField
                        id="test-id"
                        label="Test ID"
                        variant="filled"
                        fullWidth
                        helperText={nameError || 'Unique ID to the test'}
                        onChange={onNameChange}
                        onInput={onNameInput}
                        onPaste={onNamePaste}
                        onBlur={onNameBlur}
                        value={name}
                        error={Boolean(nameError)}
                    />
                </Grid>

                {allowContentEditing && (
                    // 'Add components' button
                    <Grid item xs={2} style={{ display: 'flex', margin: '24px' }}>
                        <Button
                            color='primary'
                            onClick={openAddDialog}
                            disabled={!allowAddContent}
                        >
                            Add Components
                            {!allowAddContent && <CircularProgress color='primary' size={24} />}
                        </Button>
                    </Grid>
                )}

                <Grid item xs={2} style={{ display: 'flex', margin: '24px' }}>
                    <Button
                        color='primary'
                        onClick={onExportKeys}
                    >
                        Export Keys
                    </Button>
                </Grid>
            </Grid>

            {/* Selected items */}
            <Conditional if={allowContentEditing}>
                <OutputComponentList
                    className={classes.componentList}
                    rows={selectedComponents}
                    removeItem={removeComponent}
                />
            </Conditional>

            {/* Component selector dialog */}
            <ComponentPicker
                open={isAddDialogOpen}
                onClose={() => setAddDialogOpen(false)}
                componentIds={selectedComponentIds}
                onConfirmChanges={setSelectedComponentIds}
                componentOptions={componentOptions}
            />
            {testId && !isNew && <SectionDividers DividerProps={{ className: classes.divider }}>
                <ActivityLogs
                    logId={testId}
                    logType='test'
                    reload={isSaving} />
            </SectionDividers>}
            <ScreenSaveCancelBlock
                disabled={isSaving}
                save={saveTest}
                saveDisabled={!allowSaving}
                cancelTo={`/${globalLevel.label}/tests`}
            />

            <LoadingScreen open={isLoading || isSaving} />
        </Box>
    );
}